import { IconButton } from '@mui/material'
import { Close as CloseIcon, Help as HelpIcon } from '@mui/icons-material'
import { inject, observer } from 'mobx-react'
import { useSnackbar } from 'notistack'
import * as React from 'react'
import { IStores } from '../../Stores'
import { INotification } from '../../stores/NotificationStore/NotificationStore'

interface INotifierProps {
  markNotificationShown?: (key: string) => void
  notifications?: INotification[]
  removeNotification?: (key: string) => void
  notificationConfig?: any
  shown?: boolean
  openDialog?: (notification: INotification) => void
}

const Notifier: React.FC<INotifierProps> = observer((props) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const {
    markNotificationShown,
    notifications,
    removeNotification,
    notificationConfig,
    openDialog,
  } = props

  // Stabilize the callback functions
  const stableRemoveNotification = React.useCallback(
    (key: string) => {
      closeSnackbar(key)
      removeNotification && removeNotification(key)
    },
    [removeNotification, closeSnackbar]
  )

  const stableOpenDialog = React.useCallback(
    (notification: INotification) => {
      openDialog && openDialog(notification)
    },
    [openDialog]
  )

  React.useEffect(() => {
    notifications!
      .filter((n: INotification) => !n.isShown)
      .forEach((notification: INotification) => {
        const notificationKey = notification.key
        const actions = []
        if (notification.information) {
          actions.push(
            <IconButton
              key="info"
              color="primary"
              onClick={() => stableOpenDialog(notification)}
            >
              <HelpIcon color="action" />
            </IconButton>
          )
        }
        if (
          notificationConfig.PersistAllNotifications ||
          (notificationConfig.PersistErrorNotifications &&
            notification.options.variant === 'error')
        ) {
          notification.options.autoHideDuration = undefined
          actions.push(
            <IconButton
              key="close"
              onClick={() => stableRemoveNotification(notificationKey)}
            >
              <CloseIcon />
            </IconButton>
          )
        } else {
          setTimeout(() => {
            stableRemoveNotification(notificationKey)
          }, notificationConfig.DefaultAutoHide)
        }
        notification.options.action = actions
        enqueueSnackbar(notification.message, {
          ...notification.options,
          key: notificationKey,
        })
        markNotificationShown && markNotificationShown(notificationKey)
      })
  }, [
    notifications?.length,
    enqueueSnackbar,
    markNotificationShown,
    stableRemoveNotification,
    stableOpenDialog,
    notificationConfig,
  ])

  return null
})

const InjectedNotifier = inject<IStores, INotifierProps, Partial<INotifierProps>, any>(
  (stores: IStores) => ({
    markNotificationShown: stores.notifications.markNotificationShown,
    notifications: stores.notifications.notifications,
    openDialog: stores.notifications.openDialog,
    removeNotification: stores.notifications.removeNotification,
  })
)(Notifier)

export default InjectedNotifier
export { Notifier }
